import { EQuoteStatus, IQuoteFilters } from 'types/quote.types';
import { TQuoteFiltersComp } from '../components/QuoteFilters/QuoteFilters';

const QUOTE_URL_PARAM_KEYS = {
  STATUS: 'status',
  SEARCH: 'search',
  COUNTRY: 'country',
  ORGANIZATION: 'organization',
  RESPONSIBLE: 'responsible',
  SLA: 'sla',
  TYPE: 'type',
  SORT_BY: 'sortBy',
  SORT_ORDER: 'sortOrder'
} as const;

const QUOTE_STATUS_VALUES = Object.values(EQuoteStatus);

export interface IQuoteFiltersUrlState {
  status: EQuoteStatus;
  searchText: string;
  filters: IQuoteFilters;
  quoteFiltersComp: TQuoteFiltersComp;
  sortBy?: string;
  sortOrder?: string;
}

const EMPTY_QUOTE_FILTERS_COMP: TQuoteFiltersComp = {
  country: [],
  organization: [],
  responsible: [],
  sla: [],
  type: [],
  createdAt: []
};

/**
 * Convierte un parámetro de URL en un arreglo de strings.
 * @param value - Valor del query param
 */
const parseArrayParam = (value: string | null): string[] => {
  if (!value?.trim()) return [];
  return value.split(',').filter(Boolean);
};

/**
 * Serializa un arreglo de strings para un query param.
 * @param values - Valores a serializar
 */
const serializeArrayParam = (values?: string[]): string | undefined => {
  if (!values?.length) return undefined;
  return values.join(',');
};

/**
 * Valida y normaliza el status de cotización desde la URL.
 * @param value - Valor del query param status
 */
const parseStatusParam = (value: string | null): EQuoteStatus => {
  if (value && QUOTE_STATUS_VALUES.includes(value as EQuoteStatus)) {
    return value as EQuoteStatus;
  }
  return EQuoteStatus.TO_QUOTE;
};

/**
 * Mapea los filtros del componente al formato usado por la API.
 * @param quoteFiltersComp - Filtros del componente QuoteFilters
 */
export const mapQuoteFiltersCompToApiFilters = (quoteFiltersComp: TQuoteFiltersComp): Partial<IQuoteFilters> => ({
  countryIds: quoteFiltersComp?.country,
  organizationIds: quoteFiltersComp?.organization,
  userIds: quoteFiltersComp?.responsible,
  quoteTypeIds: quoteFiltersComp?.type,
  slaRules: quoteFiltersComp?.sla
});

/**
 * Construye el estado de filtros de cotización a partir de URLSearchParams.
 * @param searchParams - Query params de la URL
 */
export const parseQuoteFiltersFromSearchParams = (searchParams: URLSearchParams): IQuoteFiltersUrlState => {
  const params = searchParams;

  const quoteFiltersComp: TQuoteFiltersComp = {
    ...EMPTY_QUOTE_FILTERS_COMP,
    country: parseArrayParam(params.get(QUOTE_URL_PARAM_KEYS.COUNTRY)),
    organization: parseArrayParam(params.get(QUOTE_URL_PARAM_KEYS.ORGANIZATION)),
    responsible: parseArrayParam(params.get(QUOTE_URL_PARAM_KEYS.RESPONSIBLE)),
    sla: parseArrayParam(params.get(QUOTE_URL_PARAM_KEYS.SLA)),
    type: parseArrayParam(params.get(QUOTE_URL_PARAM_KEYS.TYPE))
  };

  const sortBy = params.get(QUOTE_URL_PARAM_KEYS.SORT_BY) || undefined;
  const sortOrder = params.get(QUOTE_URL_PARAM_KEYS.SORT_ORDER) || undefined;
  const searchText = params.get(QUOTE_URL_PARAM_KEYS.SEARCH)?.trim() || '';

  const filters: IQuoteFilters = {
    ...mapQuoteFiltersCompToApiFilters(quoteFiltersComp),
    search: searchText || undefined,
    sortData: sortBy ? [sortBy] : undefined,
    sortValues: sortOrder ? [sortOrder] : undefined
  };

  return {
    status: parseStatusParam(params.get(QUOTE_URL_PARAM_KEYS.STATUS)),
    searchText,
    filters,
    quoteFiltersComp,
    sortBy,
    sortOrder
  };
};

/**
 * Construye el estado de filtros a partir del search de useLocation.
 * @param search - Cadena search (ej. "?status=TO_QUOTE&country=1,2")
 */
export const parseQuoteFiltersFromSearch = (search: string): IQuoteFiltersUrlState => {
  return parseQuoteFiltersFromSearchParams(new URLSearchParams(search.replace(/^\?/, '')));
};

/**
 * Construye URLSearchParams para persistir filtros en la URL (sin paginación).
 * @param state - Estado actual de filtros, status, búsqueda y ordenamiento
 */
export const buildQuoteFiltersSearchParams = (state: {
  status: EQuoteStatus;
  filters: IQuoteFilters;
  searchText: string;
}): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.status !== EQuoteStatus.TO_QUOTE) {
    params.set(QUOTE_URL_PARAM_KEYS.STATUS, state.status);
  }

  const search = state.searchText?.trim();
  if (search) {
    params.set(QUOTE_URL_PARAM_KEYS.SEARCH, search);
  }

  const country = serializeArrayParam(state.filters?.countryIds);
  if (country) params.set(QUOTE_URL_PARAM_KEYS.COUNTRY, country);

  const organization = serializeArrayParam(state.filters?.organizationIds);
  if (organization) params.set(QUOTE_URL_PARAM_KEYS.ORGANIZATION, organization);

  const responsible = serializeArrayParam(state.filters?.userIds);
  if (responsible) params.set(QUOTE_URL_PARAM_KEYS.RESPONSIBLE, responsible);

  const sla = serializeArrayParam(state.filters?.slaRules);
  if (sla) params.set(QUOTE_URL_PARAM_KEYS.SLA, sla);

  const type = serializeArrayParam(state.filters?.quoteTypeIds);
  if (type) params.set(QUOTE_URL_PARAM_KEYS.TYPE, type);

  const sortBy = state.filters?.sortData?.[0];
  const sortOrder = state.filters?.sortValues?.[0];
  if (sortBy) params.set(QUOTE_URL_PARAM_KEYS.SORT_BY, sortBy);
  if (sortOrder) params.set(QUOTE_URL_PARAM_KEYS.SORT_ORDER, sortOrder);

  return params;
};
